import { useEffect, useMemo, useRef, useState } from "react";
import { C } from "../lib/theme";
import { Btn } from "../components/Btn";
import { Card } from "../components/Card";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { ProductCard } from "../components/ProductCard";
import { useCategories } from "../hooks/useCategories";
import { useInfiniteProducts } from "../hooks/useProducts";
import { useAuth } from "../hooks/useAuth";

const EMPTY_COPY = {
  search:   (q) => q ? `No matches for "${q}"` : "Start typing to search",
  fresh:    () => "No new items today — check back soon",
  trending: () => "Nothing trending yet",
  "for-you":() => "Start shopping to see picks for you",
};

export function Search({ initial, onBack, onOpenProduct }) {
  const source = initial?.source || "search";
  const { user } = useAuth();
  const { categories } = useCategories();

  const [query, setQuery]           = useState(initial?.query || "");
  const [categoryId, setCategoryId] = useState(initial?.categoryId || "all");
  const [sort, setSort]             = useState("newest");
  const [priceMin, setPriceMin]     = useState("");
  const [priceMax, setPriceMax]     = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const inputRef = useRef(null);
  useEffect(() => {
    if (source === "search") inputRef.current?.focus();
  }, [source]);

  const { data, fetchNextPage, hasNextPage, isLoading, isError } = useInfiniteProducts({
    source,
    query,
    categoryId,
    sort,
    priceMin: priceMin === "" ? null : Number(priceMin),
    priceMax: priceMax === "" ? null : Number(priceMax),
    pageSize: 24,
    userId: user?.id || null,
  });

  const rows = useMemo(() => (data?.pages || []).flatMap((pg) => pg.rows), [data]);
  const total = data?.pages?.[0]?.total ?? rows.length;
  const title = initial?.title || (source === "search" ? "Search" : "Results");

  function clearFilters() {
    setSort("newest");
    setPriceMin("");
    setPriceMax("");
  }

  const emptyCopy = EMPTY_COPY[source]?.(query.trim()) || EMPTY_COPY.search(query.trim());

  return (
    <div>
      <div style={{
        position: "sticky", top: 56, zIndex: 20,
        background: "rgba(250,250,247,0.92)", backdropFilter: "blur(10px)",
        padding: "10px 0 12px", marginBottom: 12,
        borderBottom: "1px solid rgba(196,193,183,0.3)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onBack} style={{
            width: 34, height: 34, border: "1px solid rgba(196,193,183,0.4)",
            borderRadius: 10, background: C.white, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="arrow-left" size={16} color={C.charcoal} />
          </button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: "-0.3px" }}>{title}</div>
            <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
              {isLoading ? "Loading…" : `${total} item${total === 1 ? "" : "s"}`}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{
          flex: 1, display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderRadius: 14, background: C.white,
          border: "1px solid rgba(196,193,183,0.4)",
        }}>
          <Icon name="search" size={16} color={C.muted} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
            placeholder="Search products…"
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, fontFamily: "inherit", color: C.charcoal }}
          />
          {query && (
            <button onClick={() => setQuery("")} style={{
              border: "1px solid rgba(196,193,183,0.4)", background: C.cream,
              color: C.muted, fontSize: 10.5, fontWeight: 700, padding: "3px 7px",
              borderRadius: 6, cursor: "pointer", letterSpacing: 0.5,
            }}>ESC</button>
          )}
        </div>
        <Btn kind="outline" icon="sliders-horizontal" onClick={() => setFiltersOpen((v) => !v)}>
          Filters
        </Btn>
      </div>

      <div className="no-scrollbar" style={{ display: "flex", gap: 8, marginBottom: 14, overflowX: "auto", paddingBottom: 4 }}>
        {categories.map((c) => {
          const on = categoryId === c.id;
          return (
            <button key={c.id} onClick={() => setCategoryId(c.id)}
              style={{
                padding: "10px 16px", borderRadius: 14,
                border: `1.5px solid ${on ? C.hunter : "rgba(196,193,183,0.4)"}`,
                background: on ? C.hunter : C.white,
                color: on ? C.dust : C.slate,
                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 8,
                whiteSpace: "nowrap", fontFamily: "inherit",
              }}>
              <span style={{ fontSize: 15 }}>{c.icon || "📦"}</span>
              {c.name}
            </button>
          );
        })}
      </div>

      {filtersOpen && (
        <Card style={{ marginBottom: 14, padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>Sort</div>
              <div style={{ display: "inline-flex", background: C.cream, padding: 3, borderRadius: 10, gap: 2, border: "1px solid rgba(196,193,183,0.3)" }}>
                {[
                  ["newest", "Newest"],
                  ["priceAsc", "Price ↑"],
                  ["priceDesc", "Price ↓"],
                ].map(([v, label]) => {
                  const on = sort === v;
                  return (
                    <button key={v} onClick={() => setSort(v)} style={{
                      border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12,
                      fontWeight: 700, cursor: "pointer",
                      background: on ? C.white : "transparent", color: on ? C.hunter : C.muted,
                      boxShadow: on ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                    }}>{label}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>Price (K)</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="number" placeholder="min" value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
                  style={{ width: 100, padding: "8px 10px", border: "1px solid rgba(196,193,183,0.4)", borderRadius: 10, fontSize: 13, fontFamily: "inherit" }} />
                <span style={{ color: C.muted }}>–</span>
                <input type="number" placeholder="max" value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
                  style={{ width: 100, padding: "8px 10px", border: "1px solid rgba(196,193,183,0.4)", borderRadius: 10, fontSize: 13, fontFamily: "inherit" }} />
                <button onClick={clearFilters} style={{ marginLeft: "auto", background: "none", border: "none", color: C.hunter, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {isError && (
        <Card style={{ padding: 14, marginBottom: 12, background: C.redBg, border: `1px solid ${C.red}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>Couldn't load results</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Please try again.</div>
        </Card>
      )}

      {isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i}>
              <Skeleton h={220} r={16} />
              <div style={{ padding: "10px 4px" }}>
                <Skeleton h={14} style={{ marginBottom: 6 }} />
                <Skeleton h={10} w="60%" />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon="package" title={emptyCopy} body="Try a different keyword or clear your filters." />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
            {rows.map((p) => (<ProductCard key={p.id} product={p} onOpen={onOpenProduct} />))}
          </div>
          {hasNextPage && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
              <Btn kind="outline" onClick={() => fetchNextPage()}>Load more</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}
